// Utility function to mask client names for demo/presentation purposes
// This replaces real client names with "Client 1", "Client 2", etc.

// Map to store original client names and their masked versions
const clientNameMap = new Map<string, string>();
let clientCounter = 1;

/**
 * Masks a client name by replacing it with a generic "Client X" format
 * @param originalName - The original client name
 * @param clientId - Optional client ID to ensure consistency
 * @returns The masked client name (e.g., "Client 1", "Client 2")
 */
export function maskClientName(originalName: string, clientId?: string): string {
  if (!originalName || originalName.trim() === '') {
    return `Client ${clientCounter++}`;
  }

  // Use clientId as key if available, otherwise use original name
  const key = clientId || originalName;
  
  if (clientNameMap.has(key)) {
    return clientNameMap.get(key)!;
  }
  
  const maskedName = `Client ${clientCounter++}`;
  clientNameMap.set(key, maskedName);
  return maskedName;
}

/**
 * Resets the client name mapping (useful for testing or when starting fresh)
 */
export function resetClientNameMapping(): void {
  clientNameMap.clear();
  clientCounter = 1;
}

/**
 * Gets the original client name from a masked name (if still in memory)
 * @param maskedName - The masked client name
 * @returns The original client name or null if not found
 */
export function getOriginalClientName(maskedName: string): string | null {
  for (const [key, value] of clientNameMap.entries()) {
    if (value === maskedName) {
      return key;
    }
  }
  return null;
}
